/**
 * A horizontally scrollable reviews carousel with optional arrow controls.
 * The track scrolls natively (CSS scroll-snap); the arrows scroll it by one
 * card at a time and disable themselves at each end.
 *
 * When no arrows are rendered, mouse click-and-drag scrolling is enabled on
 * desktop (grab/grabbing cursor) so reviews can be dragged through by hand.
 *
 * When data-scroll-target is set, clicking a review card smooth-scrolls the
 * page to that element (e.g. the Judge.me reviews widget).
 */
class ReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-reviews-track]');
    this.prevButton = this.querySelector('[data-reviews-prev]');
    this.nextButton = this.querySelector('[data-reviews-next]');
    if (!this.track) return;

    this.prevButton?.addEventListener('click', () => this.scrollByCard(-1));
    this.nextButton?.addEventListener('click', () => this.scrollByCard(1));
    this.track.addEventListener('scroll', this.updateArrows, { passive: true });

    this.resizeObserver = new ResizeObserver(this.updateArrows);
    this.resizeObserver.observe(this.track);
    this.updateArrows();

    // Enable drag-to-scroll only when there are no arrow controls.
    if (!this.prevButton && !this.nextButton) {
      this.enableDragScroll();
    }

    // Clicking a card scrolls the page to the reviews section.
    this.scrollTarget = this.dataset.scrollTarget;
    if (this.scrollTarget) {
      this.classList.add('is-clickable');
      this.addEventListener('click', this.onCardClick);
    }
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
    this.track?.removeEventListener('pointerdown', this.onPointerDown);
    this.track?.removeEventListener('pointermove', this.onPointerMove);
    this.track?.removeEventListener('pointerup', this.onPointerUp);
    this.track?.removeEventListener('pointercancel', this.onPointerUp);
    this.track?.removeEventListener('click', this.onClickCapture, true);
    this.removeEventListener('click', this.onCardClick);
  }

  onCardClick = (event) => {
    // Ignore clicks that were actually the end of a drag.
    if (this.dragMoved) return;
    // Ignore the arrow controls (pointer capture can retarget the click to the
    // track, so we can't rely on the click landing on the card itself).
    if (event.target.closest?.('[data-reviews-prev], [data-reviews-next]')) return;
    const target = document.querySelector(this.scrollTarget);
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  enableDragScroll() {
    this.dragging = false;
    this.dragMoved = false;
    this.track.classList.add('is-draggable');
    this.track.addEventListener('pointerdown', this.onPointerDown);
    this.track.addEventListener('pointermove', this.onPointerMove);
    this.track.addEventListener('pointerup', this.onPointerUp);
    this.track.addEventListener('pointercancel', this.onPointerUp);
    // Swallow the click that follows a drag so links/buttons aren't triggered.
    this.track.addEventListener('click', this.onClickCapture, true);
  }

  onPointerDown = (event) => {
    // Touch/pen already scroll natively; only hijack mouse.
    if (event.pointerType !== 'mouse') return;
    this.dragging = true;
    this.dragMoved = false;
    this.startX = event.clientX;
    this.startScrollLeft = this.track.scrollLeft;
    this.track.classList.add('is-dragging');
    this.track.setPointerCapture?.(event.pointerId);
  };

  onPointerMove = (event) => {
    if (!this.dragging) return;
    const delta = event.clientX - this.startX;
    if (Math.abs(delta) > 3) this.dragMoved = true;
    this.track.scrollLeft = this.startScrollLeft - delta;
  };

  onPointerUp = (event) => {
    if (!this.dragging) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    this.track.releasePointerCapture?.(event.pointerId);
  };

  onClickCapture = (event) => {
    if (this.dragMoved) {
      event.preventDefault();
      event.stopPropagation();
      this.dragMoved = false;
    }
  };

  scrollByCard(direction) {
    const firstCard = this.track.firstElementChild;
    const styles = getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const step = firstCard ? firstCard.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  updateArrows = () => {
    if (!this.prevButton || !this.nextButton) return;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
    this.prevButton.disabled = this.track.scrollLeft <= 0;
    this.nextButton.disabled = this.track.scrollLeft >= maxScroll;
  };
}

if (!customElements.get('reviews-carousel')) {
  customElements.define('reviews-carousel', ReviewsCarousel);
}
